/**
 * Ingestion Service
 * Abstraction layer for ingestion pipeline monitoring
 */
import { api } from './api'

export const ingestionService = {
    async getLogs() {
        const res = await api.get('/admin/ingestion-logs')
        if (!res.success) throw new Error('Failed to fetch logs')

        return res.data.map(log => ({
            id: log.id,
            source: log.source,
            lastRun: log.started_at,
            completedAt: log.completed_at,
            status: log.status === 'completed' ? 'success' : (log.status === 'failed' ? 'failed' : 'warning'),
            new: log.new_count || 0,
            updated: log.updated_count || 0,
            errors: log.status === 'failed' ? 1 : 0,
            errorType: log.status === 'failed' ? 'pipeline' : null,
            failureStatus: log.status === 'failed' ? 'open' : null,
            errorMessage: log.error_message || null,
        }))
    },

    async runIngestion(sourceId, filters = {}) {
        const query = new URLSearchParams(
            Object.entries(filters).filter(([_, v]) => v)).toString()

        const res = await api.post(`/admin/ingestion/${sourceId}/run?${query}`)
        return res
    },

    /** Returns all non-terminal find_a_tender runs (oldest first). */
    async getAllActiveRuns() {
        const res = await api.get('/admin/ingestion/find_a_tender/active-runs')
        if (!res.success) throw new Error('Failed to fetch active runs')
        return res.data ?? []
    },

    /** find_a_tender runs in the background — poll this with the run_id returned by runIngestion. */
    async getRunStatus(runId) {
        const res = await api.get(`/admin/ingestion/find_a_tender/runs/${runId}`)
        if (!res.success) throw new Error('Failed to fetch run status')
        return res.data
    },

    /** Re-enqueues stuck/pending batches without re-running discovery. Safe to call on any live run. */
    async resumeRun(runId) {
        const res = await api.post(`/admin/ingestion/find_a_tender/runs/${runId}/resume`)
        if (!res.success) throw new Error('Failed to resume run')
        return res.data
    },

    /** Cancels a live find_a_tender run — stops pending/processing batches and marks it cancelled. */
    async cancelRun(runId) {
        const res = await api.delete(`/admin/ingestion/find_a_tender/runs/${runId}`)
        if (!res.success) throw new Error('Failed to cancel run')
        return res.data
    },

    async getErrors(sourceId) {
        // Filter logs for errors from a specific source
        const logs = await this.getLogs()
        return logs.filter(l => l.errors > 0 && (!sourceId || l.source === sourceId))
    },
}
